import React, { useCallback, useEffect, useMemo, useState } from 'react'

import ApiClient from '../../services/api/ApiClient'
import Spinner from '../ui/Spinner'
import SaveFooter from '../shared/SaveFooter'
import AnswerFormHeader from '../AnswerForm/AnswerFormHeader'
import ParallelStatusSelect from '../AnswerForm/ParallelStatusSelect'
import SectionBlock from '../AnswerForm/SectionBlock'
import buildMarksFormData from '../AnswerForm/buildMarksFormData'
import { computeHiddenQuestionIds, marksByQuestionId } from '../AnswerForm/conditionalVisibility'
import { pendingPinErrors } from '../AnswerForm/pendingPinErrors'
import scrollToFirstError from '../../utils/scrollToFirstError'
import { formatDate } from '../../utils/dateHelpers'

const GEO_ERROR_CODES = { 1: 'denied', 2: 'unavailable', 3: 'timeout' }
const POSITION_ACCURACY = 'position_accuracy'
const POSITION_STATUS = 'position_status'
const POSITION_ERROR = 'position_error'
const FORCED_FINISH = 'forced_finish'
const SELECTABLE_STATUSES = ['open', 'closed', 'disabled']

const initialStatus = parallel => {
  if (SELECTABLE_STATUSES.includes(parallel.status))
    return parallel.status
  return 'open'
}

const updateMark = (marks, markId, changes) => marks.map(mark => {
  if (mark.id === markId)
    return { ...mark, ...changes }
  return mark
})

const appendGeo = (formData, geo) => {
  Object.keys(geo).forEach(key => formData.append(`parallel[${key}]`, geo[key]))
}

const EditSectionParallel = () => {
  const [data, setData] = useState(null)
  const [errors, setErrors] = useState({})
  const [geo, setGeo] = useState(null)
  const [status, setStatus] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    const url = `${window.location.pathname}.json${window.location.search}`
    ApiClient.get(url).then(response => {
      setStatus(initialStatus(response.parallel))
      setData(response)
    }).catch(() => setLoadError(true))
  }, [])

  useEffect(() => {
    if (!data || !data.has_geolocation || data.readonly)
      return
    const onSuccess = ({ coords }) => setGeo({
      latitude: coords.latitude,
      longitude: coords.longitude,
      [POSITION_ACCURACY]: coords.accuracy,
      [POSITION_STATUS]: 'success'
    })
    const onError = ({ code, message }) => setGeo({
      [POSITION_STATUS]: GEO_ERROR_CODES[code] || 'unavailable',
      [POSITION_ERROR]: message
    })
    navigator.geolocation.getCurrentPosition(onSuccess, onError)
  }, [data?.has_geolocation, data?.readonly])

  const handleMarkChange = useCallback((markId, changes) => {
    setData(current => ({
      ...current,
      parallel: { ...current.parallel, marks: updateMark(current.parallel.marks, markId, changes) }
    }))
  }, [])

  const handleCancel = useCallback(() => {
    window.location.assign(data.back_url)
  }, [data])

  const handleSubmit = useCallback(async() => {
    const pending = pendingPinErrors(data.parallel.marks)
    if (Object.keys(pending).length > 0) {
      setErrors(pending)
      scrollToFirstError()
      return
    }

    const formData = new FormData()
    formData.append('parallel[status]', status)
    if (data.has_finish_resource)
      formData.append(`parallel[${FORCED_FINISH}]`, 'false')
    if (geo)
      appendGeo(formData, geo)
    buildMarksFormData(formData, 'parallel', data.parallel.marks)

    setSubmitting(true)
    setErrors({})

    try {
      const url = `${data.form_url}${window.location.search}`
      const response = await ApiClient.patchFormData(url, formData)
      if (response.success) {
        window.location.assign(response.redirect_url)
        return
      }
      setErrors(response.formatted_errors || {})
      setSubmitting(false)
      scrollToFirstError()
    } catch (error) {
      setSubmitting(false)
      setErrors({ base: [error.message] })
      scrollToFirstError()
    }
  }, [data, geo, status])

  const hidden = useMemo(() => {
    if (!data)
      return {}
    const additional = Boolean(data.parallel.is_additional)
    return computeHiddenQuestionIds(
      data.conditional_visibility, marksByQuestionId([data.parallel]), { additional }
    )
  }, [data])

  if (loadError) {
    return (
      <div className="alert alert-danger" role="alert">
        Le chargement a échoué. Veuillez recharger la page.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="tw:flex tw:justify-center tw:py-5">
        <Spinner />
      </div>
    )
  }

  return (
    <div>
      <AnswerFormHeader
        backUrl={data.back_url}
        chips={[data.collect.name, formatDate(data.draft_starting_at), data.resource.name]}
      />
      {errors.base ? (
        <div className="alert alert-danger" role="alert">
          {errors.base.join(', ')}
        </div>
      ) : null}
      <SectionBlock
        errors={errors}
        hidden={hidden}
        parallel={data.parallel}
        readonly={data.readonly}
        onMarkChange={handleMarkChange}
      />
      {data.readonly ? null : (
        <SaveFooter
          disabled={submitting}
          submitText={submitting ? 'Patientez...' : 'Sauvegarder'}
          onCancel={handleCancel}
          onSave={handleSubmit}
        >
          <ParallelStatusSelect
            disabledStatusAvailable={data.parallel.section.has_disabled_status}
            statusError={errors.status || []}
            value={status}
            onChange={setStatus}
          />
        </SaveFooter>
      )}
    </div>
  )
}

export default EditSectionParallel
